import { SetStateAction, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import OtpInput from "react-otp-input";
import { z } from "zod";
import { Button } from "components/ui/button/Button";
import { otp } from "utilities/formSchemas";
import "./authentication.scss";
import { usePostOtpValidationMutation } from "./authenticationService";
import { useDispatch, useSelector } from "react-redux";
import { ResendCooldownButton } from "components/ui/ResendCooldownButton";
import { getAuthOtpDetails, getResendCodeDeviceId, getMfaDevices } from "features/mfaSlice";
import { getCurrentUserId, getEmailAddress, selectPreferredLanguage, setCredentials, setIsLogin, setPreferredLanguage } from "features/authSlice";
import { clearCooldown } from "features/resendCooldownSlice";
import { useRedirect } from "hooks/useRedirect";
import { usePostMfaMutation } from "pages/MFA/mfaService";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import { ErrorType } from "utilities/error";
import { DEFAULT_LANGUAGE, INVALID_OTP_ERROR, LoginError, OTP_EXPIRED_ERROR } from "utilities/Constant";
import { setFSUserProperties, FLOW_CONTEXT, AUTH_OUTCOME, trackFSAuthError, trackFSAuthStep } from "utilities/full-story";
import { getLastName } from "utilities/string";
import { useNavigate } from "react-router-dom";
import Footer from "components/footer/Footer";
// ADO-535683
import { hasInviteAckDone } from "utilities/getPreferredLanguage";
import { useGoToMyLoansAfterAuth } from "hooks/useGoToMyLoansAfterAuth";

interface FormData {
  otp: string;
}

const formSchema = z.object({ otp });

export default function MFA() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const goToMyLoansAfterAuth = useGoToMyLoansAfterAuth();
  const didEnterMyLoansRef = useRef(false);
  const otpDetails = useSelector(getAuthOtpDetails);
  const userId = useSelector(getCurrentUserId);
  const emailAddress = useSelector(getEmailAddress);
  const preferredLanguage = useSelector(selectPreferredLanguage);
  useRedirect(otpDetails.id == "", "/");
  const [error, setError] = useState({ status: 0, code: "" });
  const {
    handleSubmit,
    control,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit", // Only validate on submit
    reValidateMode: "onSubmit", // Re-validate only on submit
  });

  const [sendRequest, { isSuccess, data, isLoading, isError: isOtpError }] = usePostOtpValidationMutation();
  const [resendCode] = usePostMfaMutation();

  useEffect(() => {
    // ADO-535683: skip English default when borrower acknowledged LO pre-set Spanish invite
    if (!preferredLanguage && !hasInviteAckDone()) {
      dispatch(setPreferredLanguage(DEFAULT_LANGUAGE));
    }
  }, [dispatch, preferredLanguage]);

  const onSubmit = (value: string) => {
    return sendRequest({
      otp: value,
      flowId: otpDetails.id,
      userId: userId,
    })
      .unwrap()
      .catch((error: ErrorType) => {
        trackFSAuthError(FLOW_CONTEXT.login, error);
        setError((prev) => ({
          ...prev,
          status: error.status,
          code: error.data.code.toLowerCase(),
        }));
      });
  };

  useEffect(() => {
    if (!isSuccess || !data || didEnterMyLoansRef.current) {
      return;
    }

    didEnterMyLoansRef.current = true;
    const lastName = getLastName(data.name);
    trackFSAuthStep(FLOW_CONTEXT.login, AUTH_OUTCOME.completed);
    setFSUserProperties(emailAddress, lastName.toLowerCase());
    dispatch(setCredentials(data));
    dispatch(setIsLogin(true));
    dispatch(clearCooldown());
    void goToMyLoansAfterAuth(data.id);
  }, [isSuccess, data, dispatch, goToMyLoansAfterAuth]);

  let ErrorMessage = "";

  if (isOtpError && error.status == 400) {
    if (error.code == "invalid otp") {
      ErrorMessage = INVALID_OTP_ERROR;
    } else {
      ErrorMessage = OTP_EXPIRED_ERROR;
    }
  }
  const resendDeviceId = useSelector(getResendCodeDeviceId);
  const mfaDevices = useSelector(getMfaDevices);
  const handleResendCode = () => {
    return resendCode({
      id: resendDeviceId,
      flowId: otpDetails.id,
      isResend: true,
    });
  };
  const handleGoBack = () => {
    dispatch(clearCooldown());
    navigate("/mfa", { replace: true });
  }
  if (isLoading || isSuccess) return <Overlay />;
  return (
    <>
      <div className="account">
        <form
          onSubmit={handleSubmit((data) => {
            return onSubmit(data.otp);
          })}
          className="account-form"
        >
          <div className="account-form__title">Please verify your account</div>
          <div className="account-form__digit">Enter your 6-digit code below</div>
          {isOtpError &&
            (error.status == 400 ? (
              <p className="error__message1">{ErrorMessage}</p>
            ) : (
              <div className="error__container">
                {LoginError.map((error) => {
                  return (
                    <p key={error} className="error__message2">
                      {error}
                    </p>
                  );
                })}
              </div>
            ))}
          {!ErrorMessage && isSubmitted && errors.otp && <p className="error__message3">{errors.otp.message}</p>}
          <div>
            <Controller
              name="otp"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <div className="account-form--input">
                    <OtpInput
                      inputStyle="account-form--input-field"
                      onChange={(e: SetStateAction<string>) => {
                        onChange(e);
                        if (isSubmitted) {
                          clearErrors("otp");
                        }
                      }}
                      value={value}
                      numInputs={6}
                      isInputNum
                      separator={<span className="separator">&mdash;</span>}
                      containerStyle="account-form--container-field"
                    />
                  </div>
                );
              }}
            />
          </div>
          <Button title="Continue" size="large" className="account-form__button1" />
        </form>
        {mfaDevices.length > 1 && (
          <Button
            title="Go Back"
            size="large"
            variant="outline-primary"
            className="account-form__button2"
            onClick={handleGoBack}
          />
        )}
        <ResendCooldownButton onResend={handleResendCode}>
          Resend Code
        </ResendCooldownButton>
      </div>
      <div className="mfa-account-footer">
        <Footer />
      </div>
    </>
  );
}
